import {ReactNode, useContext, useEffect, useRef, useState} from "react";
import {WebSocketContext} from "./useWebSocketServerContextProvider";
import useGlobalSettings from "./useGlobalSettings";
import {API} from "../API/api";
import {randomUUID} from "node:crypto";

interface iWebSocketData {
	type: string;
	uuid?: string;
	clientId?: string;
	roomId?: string;
	members?: {id: string, name: string}[]
	data?: any;
	storage?: any;
}


const TYPES = {
	CONNECTION: "connection",
	AUTH: "auth",
	CREATE_ROOM: "create_room",
	JOIN_ROOM: "join_room",
	LEAVE_ROOM: "leave_room",
	GET_USERS: "get_members",
	MESSAGE: "message",
	PRIVATE_MESSAGE: "private_message",

	USER_JOINED: "user_joined",
	USER_LEFT: "user_left",

	STORAGE: "storage",
	STORAGE_UPDATE: "storage_update",

	PINGPONG: "pingpong"
}

export const Rooms = {
	GLOBAL: "00000000-0000-0000-0000-000000000000"
}

export const WebSocketServer = ({ children } :{ children: ReactNode}) => {
	const { user } = useGlobalSettings()

	const socket = useRef<WebSocket|undefined>( undefined );
	const [socketHasConnection, setSocketHasConnection] = useState<boolean>( false );
	const [socketAuthSuccess, setSocketAuthSuccess] = useState<boolean>( false );
	const [forceReconnect, setForceReconnect] = useState<string>( "" )

	const [ clientId, setClientId ] = useState<string|undefined>();
	const [ roomId, setRoomId ] = useState<string|undefined>();

	const [users, setUsers] = useState<Record<string, {id: string, name: string}[]>>({})

	const [refetchUsers, setRefetchUsers] = useState<boolean>( false )
	const [refetchStoredData, setRefetchStoredData] = useState<boolean>( false )

	const [storedData, setStoredData] = useState<any>(null)

	const fetchUsers = () => {
		console.log( "===???")
		if( socket.current ) {
			console.log("fetching users", roomId)
			socket.current.send(
				JSON.stringify({
					type: TYPES.GET_USERS,
					roomId: roomId
				}));
		}
		setRefetchUsers( false )
	}
	const fetchStoredData = () => {
		if( socket.current ) {
			console.log("fetching stored data", roomId)
			socket.current.send(
				JSON.stringify({
					type: TYPES.STORAGE,
					roomId: roomId
				}));
		}
	}

	useEffect(() => {
		if( refetchUsers )
			fetchUsers();
	}, [refetchUsers]);

	useEffect(() => {
		if( refetchStoredData )
			fetchStoredData();
	}, [refetchStoredData])

	const intervalRef = useRef<ReturnType<typeof setInterval>>()
	useEffect(() => {
		if( ! socketHasConnection )
			intervalRef.current = setInterval( () => setForceReconnect( crypto.randomUUID() ), 10000 )
		else {
			clearInterval( intervalRef.current )
		}
	}, [socketHasConnection]);

	useEffect(() => {
		if( ! user.uuid )
			return
		if( socket.current )
			socket.current.close();

		const _socket = new WebSocket(API.websocket );

		_socket.onopen = () => {
			// setLog(prev => [...prev, "Verbunden mit WebSocket"]);
			// console.log("Verbunden mit WebSocket");
			socket.current = _socket;
			setSocketHasConnection( true );
		};
		_socket.onmessage = (event) => {
			const data = JSON.parse(event.data) as iWebSocketData;

			switch( data.type ){
				case TYPES.CONNECTION:
					console.log("Verbunden", data.clientId);
					setClientId( data.clientId );
					break;
				case TYPES.AUTH:
					console.log("Authentifizierung", data);
					setSocketAuthSuccess( true );
					if( data.roomId && data?.members && typeof data.roomId === "string" )
						setUsers( prev => ({ ...prev,
							[data.roomId ?? ""]: (data.members as {id: string, name: string}[] ?? []) }) );
					break;
				case TYPES.CREATE_ROOM:
					console.log("Raum erstellt mit ID: ", data );
					setRoomId( data.roomId );
					break;
				case TYPES.JOIN_ROOM:
					fetchUsers();
					fetchStoredData();

					console.log("Verbunden mit Client-ID: ", data.clientId);
					break;
				case TYPES.GET_USERS:
					if( data.roomId && data?.members ) {
						console.log("GET_USERS", data?.members )
						setUsers(prev => ({
							...prev,
							[data.roomId ?? ""]: (data.members as { id: string, name: string }[] ?? [])
						}));
					}
					break;
				case TYPES.MESSAGE:
					console.log( "Message", data )
					break;


				case TYPES.USER_JOINED:
				case TYPES.USER_LEFT:
					console.log("user joined/left")
					setRefetchUsers( true )
					break;

				case TYPES.STORAGE:
					console.log("storage", data )
					setStoredData( data.storage )
					break;

				default:
					console.log( "--->", data )
			}
		};
		_socket.onclose = () => {
			socket.current?.send(
				JSON.stringify({
					type: TYPES.LEAVE_ROOM,
					uuid: user.uuid,
					roomId: Rooms.GLOBAL
			}));
			setSocketAuthSuccess( false );
			setSocketHasConnection( false );
			console.log("Verbindung geschlossen");

		};
	}, [undefined, user.uuid, forceReconnect] );

	useEffect(() => {
		console.log( users )
	}, [users]);

	useEffect(() => {
		if( roomId )
			fetchUsers();
	}, [roomId]);

	useEffect(() => {
		if( socket.current ) {
			// console.log("Send Auth")
			socket.current.send(
				JSON.stringify({
					type: TYPES.AUTH,
					uuid: user.uuid,
					name: user.name
				}));
		}
	}, [socket.current]);

	useEffect(() => {
		if( socket.current && socketAuthSuccess ) {
			console.log( "Send Message" )
			socket.current.send(
				JSON.stringify({
					type: TYPES.MESSAGE,
					uuid: user.uuid,
					roomId: Rooms.GLOBAL,
					data: "Hallo Welt"
				}));
		}
	}, [socket.current,socketAuthSuccess])

	const Actions = {
		Room: {
			create: () => {
				console.log("Create Room 233")
				const newRoomID = crypto.randomUUID();
				socket.current?.send(
					JSON.stringify({
						type: TYPES.CREATE_ROOM,
						uuid: user.uuid,
						name: user.name,
						roomId: newRoomID
					})
				);
				setRoomId( newRoomID )
			},
			join: (uuid: string) => {
				console.log("Join Room", uuid )
				socket.current?.send(
					JSON.stringify({
						type: TYPES.JOIN_ROOM,
						uuid: user.uuid,
						roomId: uuid
					})
				);
				setRoomId( uuid );
			},
			leave: () => {
				socket.current?.send(
					JSON.stringify({
						type: TYPES.LEAVE_ROOM,
						uuid: user.uuid,
						roomId: Rooms.GLOBAL
					})
				);
				setRoomId( Rooms.GLOBAL );
			},
			storeData: (data: any) => {
				socket.current?.send(
					JSON.stringify({
						type: TYPES.STORAGE_UPDATE,
						uuid: user.uuid,
						roomId: roomId,
						data: data
					})
				);
			},
		},

		pingpong: () => {
			socket.current?.send(
				JSON.stringify({
					type: TYPES.PINGPONG,
					data: "ping"
				})
			);
		}
	}

	useEffect(() => {
		if( ! socketHasConnection ){
			return;
		}

		const pingpongInterval: ReturnType<typeof setInterval> = setInterval( () => {
			Actions.pingpong();
		}, 10000);

		return () => {
			clearInterval(pingpongInterval);
		};
	}, [socketHasConnection]);


	return <WebSocketContext.Provider value={{
		users: users ?? {},
		connected: socketHasConnection,
		RoomID: roomId,
		StoreData: storedData,
		Actions: {
			Room: {
				create: Actions.Room.create,
				join: Actions.Room.join,
				leave: Actions.Room.leave,
				storeData: Actions.Room.storeData,
			}
		}
	}} >
		{ children }
	</WebSocketContext.Provider>
}

export default () => useContext( WebSocketContext )